import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme/ThemeContext';
import { ModalBackdrop } from '../common/ModalBackdrop';
import { BolivianDateInput, BolivianTimeInput } from '../common/BolivianDateInput';
import type { CalendarEvent, Doctor } from './types';

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
};

const modalStyle: React.CSSProperties = {
  background: '#1e293b', padding: '24px', borderRadius: '12px', maxWidth: '900px', width: '94%', maxHeight: '86vh', overflow: 'auto', color: '#f1f5f9', border: '1px solid #334155'
};

const formStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px', alignItems: 'start' };
const fullWidthStyle: React.CSSProperties = { gridColumn: '1 / -1' };
const inputStyle: React.CSSProperties = { padding: '12px', border: '2px solid #334155', borderRadius: '6px', fontSize: '14px', background: '#0f172a', color: '#f1f5f9' };
const buttonStyle: React.CSSProperties = { padding: '12px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' };
const labelStyle: React.CSSProperties = { display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 };

export interface EventFormState {
  doctorId: string;
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  notes: string;
  recurrenceRule: string;
  recurrenceEndDate: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onDelete: () => void;
  form: EventFormState;
  setForm: (next: EventFormState) => void;
  editingEvent: CalendarEvent | null;
  doctors: Doctor[];
  onConvertIntake?: (event: CalendarEvent) => void;
}

export const EventModal: React.FC<Props> = ({ open, onClose, onSubmit, onDelete, form, setForm, editingEvent, doctors, onConvertIntake }) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const themedModalStyle: React.CSSProperties = {
    ...modalStyle,
    background: tokens.app.surface,
    color: tokens.app.text,
    border: `1px solid ${tokens.app.border}`
  };
  const themedInputStyle: React.CSSProperties = {
    ...inputStyle,
    border: `2px solid ${tokens.app.inputBorder}`,
    background: tokens.app.input,
    color: tokens.app.text,
    colorScheme: tokens.isLight ? 'light' : 'dark'
  };
  const themedButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: tokens.app.primary,
    color: tokens.app.primaryText
  };
  if (!open) return null;
  return (
    <ModalBackdrop className="responsive-row" style={modalOverlayStyle} onDismiss={onClose}>
      <div style={themedModalStyle} onClick={(e) => e.stopPropagation()}>
        <h3>{editingEvent ? t('calendar.modal.editEvent') : t('calendar.modal.newEvent')}</h3>
        {editingEvent?.isNewPatientIntake && onConvertIntake && (
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#92400e', marginBottom: 8 }}>
              {t('calendar.modal.intakeSlotHelp')}
            </div>
            <button
              type="button"
              style={{ ...themedButtonStyle, background: '#f59e0b' }}
              onClick={() => onConvertIntake(editingEvent)}
            >
              {t('calendar.modal.createPatientFromIntake')}
            </button>
          </div>
        )}
        <form className="responsive-grid" style={formStyle} onSubmit={onSubmit}>
          <label style={{ ...labelStyle, color: tokens.app.muted }}>
            {t('calendar.modal.doctor', 'Doctor')}
            <select style={themedInputStyle} value={form.doctorId} onChange={(e) => setForm({ ...form, doctorId: e.target.value })}>
              <option value="">{t('calendar.modal.selectDoctor')}</option>
              {doctors.map(doc => (<option key={doc.id} value={doc.id}>{doc.firstName} {doc.lastName}</option>))}
            </select>
          </label>

          <label style={{ ...labelStyle, color: tokens.app.muted }}>
            {t('calendar.modal.date', 'Date')}
            <BolivianDateInput style={themedInputStyle} value={form.date} onChange={date => setForm({ ...form, date })} required />
          </label>

          <div className="responsive-row" style={{ display: 'flex', gap: '12px' }}>
            <label style={{ ...labelStyle, color: tokens.app.muted, flex: 1 }}>
              {t('calendar.modal.startTime', 'Start time')}
              <BolivianTimeInput style={themedInputStyle} value={form.startTime} onChange={startTime => setForm({ ...form, startTime })} required />
            </label>
            <label style={{ ...labelStyle, color: tokens.app.muted, flex: 1 }}>
              {t('calendar.modal.endTime', 'End time')}
              <BolivianTimeInput style={themedInputStyle} value={form.endTime} onChange={endTime => setForm({ ...form, endTime })} required />
            </label>
          </div>

          <label style={{ ...labelStyle, color: tokens.app.muted }}>
            {t('calendar.modal.title')}
            <input style={themedInputStyle} placeholder={t('calendar.modal.title')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </label>

          <label style={{ ...labelStyle, ...fullWidthStyle, color: tokens.app.muted }}>
            {t('calendar.modal.notes')}
            <textarea style={{ ...themedInputStyle, minHeight: '100px' }} placeholder={t('calendar.modal.notes')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>

          <div className="responsive-row" style={{ ...fullWidthStyle, display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ ...labelStyle, color: tokens.app.muted, flex: 1 }}>
              {t('calendar.modal.recurrence', 'Recurrence')}
              <select
                style={themedInputStyle}
                value={form.recurrenceRule}
                onChange={(e) => setForm({ ...form, recurrenceRule: e.target.value })}
              >
                <option value="">{t('calendar.modal.noRecurrence')}</option>
                <option value="DAILY">{t('calendar.modal.daily')}</option>
                <option value="WEEKLY">{t('calendar.modal.weekly')}</option>
                <option value="MONTHLY">{t('calendar.modal.monthly')}</option>
              </select>
            </label>
            {form.recurrenceRule && (
              <label style={{ ...labelStyle, color: tokens.app.muted, flex: 1 }}>
                {t('calendar.modal.recurrenceEndDate')}
               <BolivianDateInput style={themedInputStyle} value={form.recurrenceEndDate} onChange={recurrenceEndDate => setForm({ ...form, recurrenceEndDate })} required={!!form.recurrenceRule} />
              </label>
            )}
          </div>

          <div className="responsive-row" style={{ ...fullWidthStyle, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button type="submit" style={themedButtonStyle}>{editingEvent ? t('calendar.modal.updateEvent') : t('calendar.modal.createEvent')}</button>
            {editingEvent && (<button type="button" style={{ ...themedButtonStyle, background: '#e74c3c' }} onClick={onDelete}>{t('calendar.modal.delete')}</button>)}
            <button type="button" style={{ ...themedButtonStyle, background: '#6c757d' }} onClick={onClose}>{t('calendar.modal.cancel')}</button>
          </div>
        </form>
      </div>
    </ModalBackdrop>
  );
};
